import { AssetDropdownInput } from "#src/batteries-included-components/Dropdowns";
import { formatUnknownRecordValueForDisplay } from "#src/utils/formatUnknownRecordValueForDisplay";
import {
  CheckboxInput,
  DateSelectorInput,
  DropdownInput,
  KeyValuePairList,
  TextInput,
  VariableTextInput,
} from "@validereinc/common-components";
import {
  AttributeDataType,
  ResourceLookup,
  type InputOutputSchemaPropertyConfigurationType,
} from "@validereinc/domain";
import { DateTimeValueSchemas } from "@validereinc/utilities";
import React, { type ReactElement } from "react";
import { WorkerInputFileField } from "./WorkerInputFileField";

export { normalizeWorkerRunFileInputValue } from "./WorkerInputFileField.helpers";

/**
 * Converts a record of unknown values into the format expected by KeyValueList.
 * Uploaded files render via {@link formatUnknownRecordValueForDisplay}; empty
 * values show a hyphen.
 */
export const recordToKeyValuePairs = (
  record?: Record<string, unknown> | null
): KeyValuePairList =>
  record
    ? Object.entries(record).map(([key, value]) => {
        const formatted = formatUnknownRecordValueForDisplay(value, {
          shouldHideFileIcon: true,
        });
        return {
          title: key,
          value: formatted === "" ? "-" : formatted,
        };
      })
    : [];

/**
 * Serializes a raw date form value to the string format the API expects,
 * applying `date_resolution` truncation ("year_month" → "YYYY-MM",
 * "year" → "YYYY") when present on the field definition.
 * Returns `undefined` for empty/null inputs so callers can decide how to
 * represent an absent value (omit from payload vs. explicit null).
 */
export const normalizeWorkerRunDateInputValue = (
  rawValue: unknown,
  definition: InputOutputSchemaPropertyConfigurationType
): string | undefined => {
  if (rawValue == null || rawValue === "") {
    return undefined;
  }

  const dateTimeStringValue =
    rawValue instanceof Date ? rawValue.toISOString() : String(rawValue);

  if (
    definition.data_type === AttributeDataType.DATE ||
    definition.data_type === AttributeDataType.DATE_TIME
  ) {
    if (definition.date_resolution === "year_month") {
      const yearMonthResult =
        DateTimeValueSchemas.dateTimeToYearMonth.safeParse(dateTimeStringValue);
      return yearMonthResult.success
        ? yearMonthResult.data
        : dateTimeStringValue;
    }

    if (definition.date_resolution === "year") {
      const yearResult =
        DateTimeValueSchemas.dateTimeToYear.safeParse(dateTimeStringValue);
      return yearResult.success ? yearResult.data : dateTimeStringValue;
    }
  }

  return dateTimeStringValue;
};

/**
 * Normalizes a single `lookup` worker run input (e.g. an optional facility
 * UUID) for the `POST /worker_runs` payload. A blank form value (`""`, `null`
 * or `undefined`) becomes an explicit `null` instead of an empty string, which
 * workers cannot parse as an entity ID. Any other value passes through as-is.
 */
export const normalizeWorkerRunLookupInputValue = (
  rawValue: unknown
): unknown => (rawValue == null || rawValue === "" ? null : rawValue);

export type RenderWorkerInputFieldOptionsType = {
  /** Schema property key (used for labels when `display_name` is absent). */
  schemaKey: string;
  /** React Hook Form field name (e.g. `myField` or `worker_run_input.myField`). */
  fieldName: string;
  definition: InputOutputSchemaPropertyConfigurationType;
  stringFieldSuggestions?: string[];
};

/**
 * Renders a string worker-input as `VariableTextInput` when step-variable
 * suggestions are provided, otherwise as a plain `TextInput`.
 */
const renderTextWorkerInputField = ({
  fieldName,
  label,
  isRequired,
  description,
  stringFieldSuggestions,
}: {
  fieldName: string;
  label: string;
  isRequired: boolean;
  description?: string;
  stringFieldSuggestions?: string[];
}): ReactElement => {
  if (stringFieldSuggestions != null) {
    return (
      <VariableTextInput
        key={fieldName}
        name={fieldName}
        label={label}
        isRequired={isRequired}
        suggestions={stringFieldSuggestions}
        type="text"
        description={description}
      />
    );
  }

  return (
    <TextInput
      key={fieldName}
      name={fieldName}
      label={label}
      isRequired={isRequired}
      type="text"
      description={description}
    />
  );
};

/**
 * Renders a single worker version input-schema field for use inside a RHF `Form`.
 * Shared between the global Run Worker drawer and the workflow builder run_worker step.
 * FILE / MULTI_FILE use the upload picker only when step-variable suggestions are
 * omitted; the workflow builder keeps those fields as variable text so values stay
 * strings in `worker_run_input`.
 */
export const renderWorkerInputField = (
  options: RenderWorkerInputFieldOptionsType
): ReactElement => {
  const { schemaKey, fieldName, definition, stringFieldSuggestions } = options;
  const label = definition.display_name ?? schemaKey;
  const required = definition.is_required ?? false;

  switch (definition.data_type) {
    case AttributeDataType.LOOKUP:
    case AttributeDataType.MULTI_LOOKUP: {
      const isMulti = definition.data_type === AttributeDataType.MULTI_LOOKUP;
      const lookupEntityType = definition.lookup_entity_type;
      const isAssetLookup =
        lookupEntityType === ResourceLookup.FACILITY ||
        lookupEntityType === ResourceLookup.EQUIPMENT ||
        lookupEntityType === ResourceLookup.DEVICE ||
        lookupEntityType === ResourceLookup.FLOW ||
        lookupEntityType === ResourceLookup.ASSET_GROUP;

      if (isAssetLookup) {
        return (
          <AssetDropdownInput
            key={fieldName}
            name={fieldName}
            label={label}
            isRequired={required}
            isMulti={isMulti}
            assetType={lookupEntityType}
            description={definition.description}
            isFluid
          />
        );
      }

      return (
        <TextInput
          key={fieldName}
          name={fieldName}
          label={label}
          isRequired={required}
          type="text"
          description={definition.description}
        />
      );
    }
    case AttributeDataType.PICK_LIST:
      return (
        <DropdownInput
          key={fieldName}
          name={fieldName}
          label={label}
          isRequired={required}
          options={
            definition.pick_list_values?.map((value) => ({
              label: value,
              value,
            })) ?? []
          }
          labelKey="label"
          valueKey="value"
          description={definition.description}
        />
      );
    case AttributeDataType.MULTI_PICK_LIST:
      return (
        <DropdownInput
          key={fieldName}
          name={fieldName}
          label={label}
          isRequired={required}
          isMulti
          options={
            definition.pick_list_values?.map((value) => ({
              label: value,
              value,
            })) ?? []
          }
          labelKey="label"
          valueKey="value"
          description={definition.description}
        />
      );
    case AttributeDataType.DATE_TIME:
      return (
        <DateSelectorInput
          key={fieldName}
          name={fieldName}
          label={label}
          isRequired={required}
          variant="time"
          description={definition.description}
        />
      );
    case AttributeDataType.DATE:
      return (
        <DateSelectorInput
          key={fieldName}
          name={fieldName}
          label={label}
          isRequired={required}
          variant="day"
          description={definition.description}
        />
      );
    case AttributeDataType.BOOLEAN:
      return (
        <CheckboxInput
          key={fieldName}
          name={fieldName}
          label={label}
          isRequired={required}
          description={definition.description}
        />
      );
    case AttributeDataType.NUMBER:
    case AttributeDataType.INTEGER:
      return (
        <TextInput
          key={fieldName}
          name={fieldName}
          label={label}
          type="number"
          isRequired={required}
          description={definition.description}
        />
      );
    case AttributeDataType.FILE:
    case AttributeDataType.MULTI_FILE:
      if (stringFieldSuggestions == null) {
        return (
          <WorkerInputFileField
            key={fieldName}
            name={fieldName}
            schemaKey={schemaKey}
            label={label}
            isRequired={required}
            description={definition.description}
            allowMultiple={
              definition.data_type === AttributeDataType.MULTI_FILE
            }
          />
        );
      }

      return renderTextWorkerInputField({
        fieldName,
        label,
        isRequired: required,
        description: definition.description,
        stringFieldSuggestions,
      });
    default:
      return renderTextWorkerInputField({
        fieldName,
        label,
        isRequired: required,
        description: definition.description,
        stringFieldSuggestions,
      });
  }
};
